<template>
  <fragment>
    <wx-form-container class="wizard">
      <v-form ref="downtimeReasonForm" lazy-validation>
        <fieldset>
          <legend class="legend-header top-legend">{{ $t("common.information") }}</legend>
          <v-row>
            <v-col cols="12" sm="6">
              <!-- - - - - - - - - - - -       Code       - - - - - - - - - - - - - - -->
              <wx-text-field
                v-model.trim="reasonCode"
                :label="$t('downtimeReason.reasonCode') + ' *'"
                :rules="[() => validateCode()]"
                :counter="downtimeReasonCodeMaxLength"
                :maxlength="downtimeReasonCodeMaxLength"
                :error="reasonCodeError"
                :error-messages="reasonCodeErrorMessages"
              />
            </v-col>
            <v-col cols="12">
              <!-- - - - - - - - - - - -       Name       - - - - - - - - - - - - - - -->
              <wx-text-field
                v-model.trim="reasonName"
                :rules="[() => validateName()]"
                :label="$t('downtimeReason.reasonName') + ' *'"
                :counter="downtimeReasonNameMaxLength"
                :maxlength="downtimeReasonNameMaxLength"
              />
            </v-col>
            <v-col cols="12">
              <!-- - - - - - - - - - - -     Production Units     - - - - - - - - - - - - - - -->
              <wx-production-unit-multi-selector
                v-model="selectedProductionUnits"
                :is-multiple-factories="false"
                :available-items="sortedProductionUnits"
              />
            </v-col>
          </v-row>
        </fieldset>
        <fieldset>
          <legend class="legend-header">{{ $t("downtimeReason.reasonType") }}</legend>
          <v-row>
            <v-col cols="12">
              <!-- - - - - - - - - - - -       Type       - - - - - - - - - - - - - - -->
              <v-radio-group v-model="reasonType" :rules="[() => validateReasonType(reasonType)]">
                <v-radio :label="$t('downtimeReason.planned')" value="planned" />
                <v-radio :label="$t('downtimeReason.unplanned')" value="unplanned" />
              </v-radio-group>
            </v-col>
            <v-col cols="12">
              <!-- - - - - - - - -        Target Duration       - - - - - - - - - - - -->
              <div v-if="isPlanned()">
                <span class="mb-3 mr-1">{{ $t("downtimeReason.wizard.targetDuration") }}</span>
                <wx-contextualized-help :tooltip-text="$t('downtimeReason.wizard.targetDurationTooltip')" />
                <wx-timer-duration-input
                  v-model="targetDuration"
                  :optional="true"
                  :title="$t('common.durationInputWithFormat')"
                  :rules="[() => validateDuration()]"
                  :error-messages="[]"
                  class="mt-2 input-target-duration"
                />
              </div>
              <v-alert v-if="isTypeChanged()" color="warning" class="py-2" text outlined>
                <p class="mb-0 wx-typo-sm">{{ $t("downtimeReason.wizard.reasonEdition.typeUpdateWarning") }}</p>
              </v-alert>
            </v-col>
          </v-row>
        </fieldset>
        <form-footer-actions :click-submit="submit" :click-cancel="back" />
      </v-form>
    </wx-form-container>
  </fragment>
</template>

<script>
import FormFooterActions from "@/components/ui/FormFooterActions";
import WxFormContainer from "@/components/ui/WxFormContainer";
import WxTextField from "@/components/ui/WxTextField";
import WxProductionUnitMultiSelector from "@/components/ui/WxProductionUnitMultiSelector";
import WxTimerDurationInput from "@/components/ui/WxTimerDurationInput";
import DowntimeReasonService from "@/components/downtimereason/DowntimeReasonService";
import ErrorHandling from "@/components/ErrorHandling";
import RouteService from "@/router/RouteService";
import Validations from "@/components/validations";
import { mapActions, mapGetters } from "vuex";
import { compareName } from "@/components/SortUtils";
import WxContextualizedHelp from "@/components/ui/WxContextualizedHelp";
import DurationUtils from "@/components/DurationUtils";

export default {
  name: "DowntimeReasonWizard",
  components: {
    FormFooterActions,
    WxFormContainer,
    WxTextField,
    WxProductionUnitMultiSelector,
    WxTimerDurationInput,
    WxContextualizedHelp,
  },
  props: {
    parentCategoryId: {
      type: String,
      required: true,
    },
    initialReason: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      id: null,
      reasonCode: null,
      reasonName: null,
      reasonCodeError: false,
      reasonCodeErrorMessages: [],
      reasonType: "planned",
      targetDuration: "",
      selectedProductionUnits: [],
      downtimeReasonCodeMaxLength: Validations.downtimeReasonCodeMaxLength,
      downtimeReasonNameMaxLength: Validations.downtimeReasonNameMaxLength,
      targetDurationRegex: /^[0-9]{2}:[0-9]{2}$/,
    };
  },
  watch: {
    initialReason() {
      if (this.initialReason) {
        this.id = this.initialReason.id;
        this.reasonCode = this.initialReason.code;
        this.reasonName = this.initialReason.name;
        this.reasonType = this.initialReason.planned ? "planned" : "unplanned";
        this.targetDuration = DurationUtils.secondsToHHMM(this.initialReason.target_duration_in_seconds);
        if (this.initialReason.production_unit_ids) {
          let puList = [];
          this.initialReason.production_unit_ids.forEach((puId) => {
            const pu = this.activeFactoryProductionUnits.find((pu) => pu.id === puId);
            if (pu) {
              puList.push(pu);
            }
          });
          this.selectedProductionUnits = puList;
        } else {
          this.selectedProductionUnits = [];
        }
      }
    },
  },
  computed: {
    ...mapGetters("navigation", ["activeFactoryId", "activeFactoryProductionUnits"]),
    ...mapGetters("user", ["theme"]),
    reasonCodeLabel() {
      return this.$t("downtimeReason.reasonCode") + " *";
    },
    reasonNameLabel() {
      return this.$t("downtimeReason.reasonName") + " *";
    },
    plannedReasonOptionLabel() {
      return this.$t("downtimeReason.planned");
    },
    unplannedReasonOptionLabel() {
      return this.$t("downtimeReason.unplanned");
    },
    sortedProductionUnits() {
      if (this.activeFactoryProductionUnits && this.activeFactoryProductionUnits.length > 0) {
        let puList = [...this.activeFactoryProductionUnits];
        puList.sort(compareName);
        return puList;
      }
      return [];
    },
  },
  methods: {
    ...mapActions("dashboard", ["fetchAvailableDowntimeReasons"]),
    ...mapActions("operation", ["showOperationSuccess", "showOperationError"]),
    getRouteBack() {
      return RouteService.toDowntimeReasons(this.parentCategoryId);
    },
    back() {
      return this.$router.push(this.getRouteBack());
    },
    isTypeChanged() {
      if (this.initialReason) {
        const initialType = this.initialReason.planned ? "planned" : "unplanned";
        return initialType !== this.reasonType;
      }
      return false;
    },
    isPlanned() {
      return this.reasonType === "planned";
    },
    validateCode() {
      if (!this.reasonCode) {
        return this.$t("downtimeReason.errors.noReasonCode");
      }
      if (this.reasonCode.trim().length > this.downtimeReasonCodeMaxLength) {
        return this.$t("downtimeReason.errors.reasonCodeTooLong");
      }
      this.reasonCodeError = false;
      this.reasonCodeErrorMessages = [];
      return true;
    },
    validateName() {
      if (!this.reasonName) {
        return this.$t("downtimeReason.errors.noReasonName");
      }
      if (this.reasonName.trim().length > this.downtimeReasonNameMaxLength) {
        return this.$t("downtimeReason.errors.reasonNameTooLong");
      }
      return true;
    },
    validateReasonType() {
      if (!this.reasonType) {
        return this.$t("downtimeReason.errors.noReasonType");
      }
      if (this.reasonType !== "planned" && this.reasonType !== "unplanned") {
        return this.$t("downtimeReason.errors.noReasonType");
      }
      return true;
    },
    validateProductionUnits() {
      if (!this.selectedProductionUnits || this.selectedProductionUnits.length === 0) {
        return this.$t("downtimeReason.errors.noProductionUnits");
      }
      return true;
    },
    validateDuration() {
      if (this.targetDuration === null || this.targetDuration.length === 0) return true;

      if (!this.targetDurationRegex.test(String(this.targetDuration).toLowerCase())) {
        return this.$t("downtimeReason.errors.invalidDuration");
      }
      const parts = this.targetDuration.split(":");
      const hour = Number(parts[0]);
      if (hour < 0 || hour > 23) {
        return this.$t("downtimeReason.errors.invalidDuration");
      }
      const minute = Number(parts[1]);
      if (minute < 0 || minute > 59) {
        return this.$t("downtimeReason.errors.invalidDuration");
      }
      if (hour === 0 && minute === 0) {
        return this.$t("downtimeReason.errors.durationUnderLimit");
      }
      return true;
    },
    isCreation() {
      return this.id === null;
    },
    isEdition() {
      return this.id !== null;
    },
    submit() {
      if (this.parentCategoryId && this.parentCategoryId.length > 0) {
        if (this.isCreation()) {
          this.create();
        }
        if (this.isEdition()) {
          this.update();
        }
      } else {
        let messages = [
          this.$t("downtimeReason.errors.noParentCategory") +
            " " +
            this.$t("downtimeReason.errors.reasonNoParentCategoryHint"),
        ];
        this.showOperationError(messages);
      }
    },
    create() {
      const isValid = this.$refs.downtimeReasonForm.validate();
      if (isValid && this.parentCategoryId) {
        const targetDurationInSeconds = this.isPlanned() ? DurationUtils.hhmmToSeconds(this.targetDuration) : null;
        let newReason = {
          code: this.reasonCode,
          name: this.reasonName,
          category_id: this.parentCategoryId,
          planned: this.reasonType === "planned",
          target_duration_in_seconds: targetDurationInSeconds,
          production_unit_ids: this.selectedProductionUnits.map((pu) => pu.id),
        };
        DowntimeReasonService.createReason(this.activeFactoryId, newReason)
          .then((response) => this.handleReasonCreationResponse(response, newReason))
          .catch((error) => this.handleReasonOperationError(error.response));
      }
    },
    handleReasonCreationResponse(httpResponse, newReason) {
      if (httpResponse.status === 201) {
        this.fetchAvailableDowntimeReasons({});
        this.showOperationSuccess(
          this.$t("downtimeReason.successfullyReasonCreated", {
            code: newReason.code,
            name: newReason.name,
          }),
        );
        this.back();
      }
    },
    update() {
      const isValid = this.$refs.downtimeReasonForm.validate();
      if (isValid && this.parentCategoryId) {
        const targetDurationInSeconds = this.isPlanned() ? DurationUtils.hhmmToSeconds(this.targetDuration) : null;
        let updatedReason = {
          id: this.id,
          code: this.reasonCode,
          name: this.reasonName,
          category_id: this.parentCategoryId,
          planned: this.reasonType === "planned",
          target_duration_in_seconds: targetDurationInSeconds,
          production_unit_ids: this.selectedProductionUnits.map((pu) => pu.id),
        };
        DowntimeReasonService.updateReason(this.id, updatedReason)
          .then((response) => this.handleReasonUpdateResponse(response, updatedReason))
          .catch((error) => this.handleReasonOperationError(error.response));
      }
    },
    handleReasonUpdateResponse(httpResponse, updatedReason) {
      if (httpResponse.status === 200) {
        this.fetchAvailableDowntimeReasons({});
        this.showOperationSuccess(
          this.$t("downtimeReason.successfullyReasonUpdated", {
            code: updatedReason.code,
            name: updatedReason.name,
          }),
        );
        this.back();
      }
    },
    handleReasonOperationError(httpResponse) {
      const errorMessage = ErrorHandling.buildErrorsMessages(httpResponse, this.getErrorMessage);
      if (httpResponse.status === 409) {
        this.reasonCodeError = true;
        this.reasonCodeErrorMessages = errorMessage;
      } else {
        this.showOperationError(ErrorHandling.buildErrorsMessages(httpResponse, this.getErrorMessage));
      }
    },
    getErrorMessage(code, args) {
      switch (code) {
        case "CS_DWNR_PUT_20001":
        case "CS_DWNR_POST_20001": {
          return this.$t("downtimeReason.errors.duplicateReason");
        }
        case "CS_DWNR_PUT_20009": {
          return this.$t("downtimeReason.errors.cannotSwitchToUnplanned", {
            reasonCode: args.reason_code,
            reasonName: args.reason_name,
            workShiftNames: args.work_shift_names,
          });
        }
        default: {
          return this.$t("common.errors.default", { code: code });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.action-container {
  display: flex;
  margin-bottom: 20px;
}

.form-box {
  max-width: 70%;
  padding: var(--box-padding-admin);
  background-color: var(--color-element-layer1);
  box-shadow: var(--box-shadow-small-elevation);
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--color-border-theme);

  h2 {
    margin-bottom: 20px;
  }
}

::v-deep legend.v-label {
  font-size: 1rem;
}

::v-deep .v-input--radio-group {
  margin-top: 0;
  padding-top: 0;
}

::v-deep .v-input--radio-group--column .v-input--radio-group__input {
  align-items: flex-start;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}
.input-target-duration {
  min-width: 180px;
  max-width: 220px;
}
</style>
